/**
 * TV/Kiosk Mode — fullscreen display optimized for wall-mounted monitors.
 *
 * Features:
 *   - Fullscreen API (hides browser chrome)
 *   - Hides app chrome (header, sub-header, side panels, floating overlays)
 *   - Maximizes map + situation board (bottom panel)
 *   - Auto-rotates between regional camera presets on a configurable interval
 *   - Wake Lock API (prevents screen dimming)
 *   - ESC to exit
 *
 * Activates via: eventBus 'tv:enter' or direct call to enterTvMode()
 * Exits via: ESC key, eventBus 'tv:exit', or direct call to exitTvMode()
 */

import { eventBus } from '@/core/events';
import { getMap } from '@/map/engine';

/** Camera presets for auto-rotation. */
const ROTATION_PRESETS: { label: string; center: [number, number]; zoom: number }[] = [
  { label: 'Global', center: [20, 20], zoom: 2 },
  { label: 'Europe', center: [15, 50], zoom: 4 },
  { label: 'Middle East', center: [45, 28], zoom: 4.5 },
  { label: 'Asia-Pacific', center: [120, 25], zoom: 3.5 },
  { label: 'Americas', center: [-80, 15], zoom: 3 },
  { label: 'Africa', center: [20, 0], zoom: 3.5 },
];

/** Auto-rotation interval options in seconds. */
export type RotationInterval = 30 | 60 | 120;

interface TvModeState {
  active: boolean;
  rotationTimer: ReturnType<typeof setInterval> | null;
  rotationIndex: number;
  rotationInterval: RotationInterval;
  wakeLock: WakeLockSentinel | null;
  hintElement: HTMLElement | null;
  cleanupFns: (() => void)[];
}

const state: TvModeState = {
  active: false,
  rotationTimer: null,
  rotationIndex: 0,
  rotationInterval: 60,
  wakeLock: null,
  hintElement: null,
  cleanupFns: [],
};

/**
 * Enter TV/Kiosk mode.
 * @param rotationInterval Seconds between camera rotations (30, 60, or 120).
 */
export async function enterTvMode(rotationInterval: RotationInterval = 60): Promise<void> {
  if (state.active) return;
  state.active = true;
  state.rotationInterval = rotationInterval;
  state.rotationIndex = 0;

  // Add CSS class to body for style overrides
  document.body.classList.add('tv-mode-active');

  // Enter fullscreen
  try {
    await document.documentElement.requestFullscreen();
  } catch {
    // Fullscreen may be blocked — continue without it
  }

  // Acquire wake lock (prevent screen dimming)
  await acquireWakeLock();

  // Show exit hint (fades after 5s)
  showExitHint();

  // Start auto-rotation
  startRotation();

  // Listen for ESC key
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      exitTvMode();
    }
  };
  document.addEventListener('keydown', onKeyDown);
  state.cleanupFns.push(() => document.removeEventListener('keydown', onKeyDown));

  // Listen for fullscreen exit (user pressed ESC in browser)
  const onFullscreenChange = (): void => {
    if (!document.fullscreenElement && state.active) {
      exitTvMode();
    }
  };
  document.addEventListener('fullscreenchange', onFullscreenChange);
  state.cleanupFns.push(() => document.removeEventListener('fullscreenchange', onFullscreenChange));

  // Re-acquire wake lock if page regains visibility
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && state.active) {
      acquireWakeLock();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  state.cleanupFns.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));

  // Resize map after layout changes settle
  setTimeout(() => {
    const map = getMap();
    if (map) map.resize();
  }, 300);

  console.log(`[Sentinel] TV mode entered (rotation: ${rotationInterval}s)`);
}

/** Exit TV/Kiosk mode and restore normal UI. */
export async function exitTvMode(): Promise<void> {
  if (!state.active) return;
  state.active = false;

  // Stop auto-rotation
  stopRotation();

  // Remove CSS class
  document.body.classList.remove('tv-mode-active');

  // Remove exit hint
  if (state.hintElement) {
    state.hintElement.remove();
    state.hintElement = null;
  }

  // Release wake lock
  releaseWakeLock();

  // Exit fullscreen
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch {
      // Already exited
    }
  }

  // Clean up listeners
  for (const fn of state.cleanupFns) fn();
  state.cleanupFns = [];

  // Resize map after layout restores
  setTimeout(() => {
    const map = getMap();
    if (map) map.resize();
  }, 300);

  console.log('[Sentinel] TV mode exited');
}

/** Check whether TV mode is currently active. */
export function isTvModeActive(): boolean {
  return state.active;
}

// ─── Auto-Rotation ──────────────────────────────────────────────────

function startRotation(): void {
  stopRotation();

  // Fly to the first preset immediately
  flyToPreset(state.rotationIndex);

  state.rotationTimer = setInterval(() => {
    state.rotationIndex = (state.rotationIndex + 1) % ROTATION_PRESETS.length;
    flyToPreset(state.rotationIndex);
  }, state.rotationInterval * 1000);
}

function stopRotation(): void {
  if (state.rotationTimer) {
    clearInterval(state.rotationTimer);
    state.rotationTimer = null;
  }
}

function flyToPreset(index: number): void {
  const preset = ROTATION_PRESETS[index];
  if (!preset) return;

  const map = getMap();
  if (!map) return;

  map.flyTo({
    center: preset.center,
    zoom: preset.zoom,
    duration: 2000,
    essential: true,
  });
}

// ─── Wake Lock ──────────────────────────────────────────────────────

async function acquireWakeLock(): Promise<void> {
  if (!('wakeLock' in navigator)) return;

  try {
    state.wakeLock = await navigator.wakeLock.request('screen');
  } catch {
    // Wake lock not available (e.g. page not visible)
  }
}

function releaseWakeLock(): void {
  if (state.wakeLock) {
    state.wakeLock.release().catch(() => {});
    state.wakeLock = null;
  }
}

// ─── Exit Hint ──────────────────────────────────────────────────────

function showExitHint(): void {
  const hint = document.createElement('div');
  hint.className = 'tv-mode-exit-hint';
  hint.textContent = 'Press ESC to exit TV mode';
  document.body.appendChild(hint);
  state.hintElement = hint;

  // Fade out after 5 seconds
  setTimeout(() => {
    if (hint.parentElement) {
      hint.style.opacity = '0';
      setTimeout(() => hint.remove(), 2000);
    }
  }, 5000);
}

// ─── Initialization ─────────────────────────────────────────────────

/** Wire up event bus listeners for TV mode. Returns cleanup function. */
export function initTvMode(): () => void {
  const unsub1 = eventBus.on('tv:enter', () => {
    enterTvMode();
  });
  const unsub2 = eventBus.on('tv:exit', () => {
    exitTvMode();
  });

  return () => {
    unsub1();
    unsub2();
    if (state.active) {
      exitTvMode();
    }
  };
}
